/** Notifications: типы + REST API-клиенты.
 *
 *  Контракт совпадает с `apps.notifications.views`:
 *  GET    /api/notifications/?scope=active|all&location_id=&unread_only=&include_resolved=&cursor=&limit=
 *  GET    /api/notifications/summary/?location_id=
 *  POST   /api/notifications/<id>/mark-read/
 *  POST   /api/notifications/mark-all-read/?scope=&location_id=
 *  GET    /api/notifications/me/settings/
 *  PATCH  /api/notifications/me/settings/  body: { <source_code>: { enabled: bool } }
 */
import { apiRequest } from "~/lib/apiRequest";

export type NotificationScope = "active" | "all";
export type NotificationSeverity = "INFO" | "WARN" | "URGENT";

export interface NotificationAction {
  code: string;
  method: "GET" | "POST" | "PATCH" | "DELETE";
  url: string;
}

export interface Notification {
  id: number;
  source_code: string;
  source_object_type: string;
  source_object_id: number;
  account_id: number;
  location_id: number | null;
  recipient_id: number | null;
  payload: Record<string, unknown>;
  severity: NotificationSeverity;
  created_at: string;
  resolved_at: string | null;
  is_read: boolean | null;
  label_key: string | null;
  actions: NotificationAction[];
}

export interface NotificationsListResponse {
  items: Notification[];
  next_cursor: string | null;
}

export interface NotificationsSummary {
  unread_active: number;
  unread_all: number;
}

export interface NotificationSettingItem {
  code: string;
  label_key: string;
  default_severity: NotificationSeverity;
  enabled: boolean;
}

export interface NotificationSettingsResponse {
  items: NotificationSettingItem[];
}

export interface FetchNotificationsParams {
  scope?: NotificationScope;
  locationId?: number | null;
  unreadOnly?: boolean;
  includeResolved?: boolean;
  limit?: number;
  cursor?: string | null;
}

export async function fetchNotifications(
  params: FetchNotificationsParams = {},
): Promise<NotificationsListResponse> {
  const q: Record<string, string | number> = {};
  if (params.scope) q.scope = params.scope;
  if (params.locationId !== undefined && params.locationId !== null) {
    q.location_id = params.locationId;
  }
  if (params.unreadOnly) q.unread_only = 1;
  if (params.includeResolved) q.include_resolved = 1;
  if (params.limit) q.limit = params.limit;
  if (params.cursor) q.cursor = params.cursor;
  return apiRequest<NotificationsListResponse>("GET", "api/notifications/", q);
}

export async function fetchSummary(
  locationId?: number | null,
): Promise<NotificationsSummary> {
  const q: Record<string, number> = {};
  if (locationId !== undefined && locationId !== null) q.location_id = locationId;
  return apiRequest<NotificationsSummary>("GET", "api/notifications/summary/", q);
}

export async function markRead(id: number): Promise<{ ok: boolean }> {
  return apiRequest<{ ok: boolean }>("POST", `api/notifications/${id}/mark-read/`);
}

export async function markAllRead(
  scope: NotificationScope,
  locationId?: number | null,
): Promise<{ ok: boolean; marked: number }> {
  const q: Record<string, string | number> = { scope };
  if (locationId !== undefined && locationId !== null) q.location_id = locationId;
  const qs = new URLSearchParams(
    Object.entries(q).map(([k, v]) => [k, String(v)]),
  ).toString();
  return apiRequest<{ ok: boolean; marked: number }>(
    "POST",
    `api/notifications/mark-all-read/?${qs}`,
  );
}

export async function fetchSettings(): Promise<NotificationSettingsResponse> {
  return apiRequest<NotificationSettingsResponse>(
    "GET",
    "api/notifications/me/settings/",
  );
}

export async function updateSettings(
  patch: Record<string, { enabled: boolean }>,
): Promise<NotificationSettingsResponse> {
  return apiRequest<NotificationSettingsResponse>(
    "PATCH",
    "api/notifications/me/settings/",
    patch,
  );
}
